import type { Task, Event, Suggestion, Message, NotificationTemplate } from '../types/mockup'
import { parseNaturalCommand, type ParsedCommand } from './naturalLanguageParser'
import { suggestOptimalTime } from './scheduleUtils'

export interface SimulationResult {
  success: boolean
  message: string
  updatedTasks?: Task[]
  suggestion?: Suggestion
  notifications?: NotificationTemplate[]
  changes?: string[]
}

export const simulateScheduleChange = (
  command: string,
  event: Event,
  currentTime: Date = new Date()
): SimulationResult => {
  const parsedCommand = parseNaturalCommand(command, { event, currentTime })
  
  switch (parsedCommand.type) {
    case 'delay':
      return simulateDelay(parsedCommand, event, currentTime)
    case 'move':
      return simulateMove(parsedCommand, event, currentTime)
    case 'mark':
      return simulateStatusUpdate(parsedCommand, event)
    case 'assign':
      return simulateAssignment(parsedCommand, event)
    case 'notify':
      return simulateNotification(parsedCommand, event)
    case 'query':
      return simulateQuery(parsedCommand, event, currentTime)
    case 'add':
      return simulateAddTask(parsedCommand, event)
    case 'remove':
      return simulateRemoveTask(parsedCommand, event)
    default:
      return {
        success: false,
        message: 'Command not recognized. Type "help" for available commands.'
      }
  }
}

function simulateDelay(
  command: ParsedCommand,
  event: Event,
  currentTime: Date
): SimulationResult {
  const { target } = command
  const minutes = (target?.time || 15) * (target?.unit === 'hours' ? 60 : 1)
  
  // Find affected tasks
  const affectedTasks: Task[] = []
  if (target?.task) {
    const task = event.tasks.find(t => t.name === target.task)
    if (task && !task.isAnchor) {
      affectedTasks.push(task)
    }
  } else {
    // Delay all non-anchor tasks after current time
    affectedTasks.push(...event.tasks.filter(t => 
      !t.isAnchor && t.startTime > currentTime
    ))
  }
  
  if (affectedTasks.length === 0) {
    return {
      success: false,
      message: 'No tasks can be delayed (anchor tasks cannot be moved)'
    }
  }
  
  // Create updated tasks
  const updatedTasks = event.tasks.map(task => {
    if (affectedTasks.includes(task)) {
      return {
        ...task,
        startTime: new Date(task.startTime.getTime() + minutes * 60000),
        endTime: new Date(task.endTime.getTime() + minutes * 60000),
        delayMinutes: (task.delayMinutes || 0) + minutes
      }
    }
    return task
  })
  
  // Find cascading changes
  const cascadingChanges: string[] = []
  affectedTasks.forEach(task => {
    const dependents = event.tasks.filter(t => t.dependencies?.includes(task.id))
    if (dependents.length > 0) {
      cascadingChanges.push(`${dependents.length} dependent task(s) will also be delayed`)
    }
  })
  
  // Create suggestion
  const suggestion: Suggestion = {
    id: `sug-${Date.now()}`,
    type: 'schedule_change',
    title: `Delay ${affectedTasks.length} task(s) by ${minutes} minutes`,
    description: target?.task 
      ? `Delay "${target.task}" and dependent tasks by ${minutes} minutes`
      : `Delay all upcoming tasks by ${minutes} minutes`,
    understanding: command.messageUnderstandingScore > 0.7 ? 'high' : 'medium',
    source: 'Schedule Simulator',
    createdAt: new Date(),
    impact: {
      tasksAffected: affectedTasks.map(t => t.id),
      suppliersAffected: [...new Set(affectedTasks.map(t => t.supplierId).filter(Boolean) as string[])],
      minutesImpact: minutes,
      cascadingChanges
    },
    proposedChanges: {
      taskUpdates: affectedTasks.map(task => ({
        id: task.id,
        startTime: new Date(task.startTime.getTime() + minutes * 60000),
        endTime: new Date(task.endTime.getTime() + minutes * 60000)
      }))
    },
    status: 'pending'
  }
  
  return {
    success: true,
    message: `Proposed: Delay ${affectedTasks.length} task(s) by ${minutes} minutes`,
    updatedTasks,
    suggestion,
    changes: cascadingChanges
  }
}

function simulateMove(
  command: ParsedCommand,
  event: Event,
  currentTime: Date
): SimulationResult {
  const { target } = command
  
  if (!target?.task) {
    return {
      success: false,
      message: 'Please specify which task to move'
    }
  }
  
  const task = event.tasks.find(t => t.name === target.task)
  if (!task) {
    return {
      success: false,
      message: `Task "${target.task}" not found`
    }
  }
  
  if (task.isAnchor) {
    return {
      success: false,
      message: 'Anchor tasks cannot be moved'
    }
  }
  
  // Calculate new time
  const baseTime = new Date(currentTime)
  baseTime.setHours(0, 0, 0, 0)
  const newStartTime = new Date(baseTime.getTime() + (target.time || 0) * 60000)
  const newEndTime = new Date(newStartTime.getTime() + task.duration * 60000)
  
  // Check for conflicts
  const conflicts = event.tasks.filter(t => {
    if (t.id === task.id) return false
    return (
      (newStartTime >= t.startTime && newStartTime < t.endTime) ||
      (newEndTime > t.startTime && newEndTime <= t.endTime)
    )
  })
  
  const suggestion: Suggestion = {
    id: `sug-${Date.now()}`,
    type: 'schedule_change',
    title: `Move "${task.name}"`,
    description: `Reschedule task to ${newStartTime.toLocaleTimeString()}`,
    understanding: conflicts.length === 0 ? 'high' : 'medium',
    source: 'Schedule Simulator',
    createdAt: new Date(),
    impact: {
      tasksAffected: [task.id],
      suppliersAffected: task.supplierId ? [task.supplierId] : [],
      minutesImpact: Math.abs(newStartTime.getTime() - task.startTime.getTime()) / 60000,
      cascadingChanges: conflicts.length > 0 
        ? [`Conflicts with ${conflicts.length} other task(s)`]
        : []
    },
    proposedChanges: {
      taskUpdates: [{
        id: task.id,
        startTime: newStartTime,
        endTime: newEndTime
      }]
    },
    status: 'pending'
  }
  
  return {
    success: true,
    message: `Proposed: Move "${task.name}" to ${newStartTime.toLocaleTimeString()}`,
    suggestion,
    changes: conflicts.map(c => `Conflicts with "${c.name}"`)
  }
}

function simulateStatusUpdate(
  command: ParsedCommand,
  event: Event
): SimulationResult {
  const { target } = command
  
  if (!target?.task) {
    return {
      success: false,
      message: 'Please specify which task to update'
    }
  }
  
  const task = event.tasks.find(t => t.name === target.task)
  if (!task) {
    return {
      success: false,
      message: `Task "${target.task}" not found`
    }
  }
  
  const newStatus = target.status || 'complete'
  
  // Create notification for status change
  const notifications: NotificationTemplate[] = []
  if (task.supplierId) {
    notifications.push({
      recipientId: task.supplierId,
      recipientType: 'supplier',
      channel: 'both',
      subject: `Task Status Update: ${task.name}`,
      message: `Task "${task.name}" has been marked as ${newStatus.replace('_', ' ')}`,
      priority: newStatus === 'delayed' || newStatus === 'at_risk' ? 'urgent' : 'normal'
    })
  }
  
  return {
    success: true,
    message: `✅ Marked "${task.name}" as ${newStatus.replace('_', ' ')}`,
    notifications,
    changes: [`Task status updated to ${newStatus}`]
  }
}

function simulateAssignment(
  command: ParsedCommand,
  event: Event
): SimulationResult {
  const { target } = command
  
  if (!target?.task) {
    return {
      success: false,
      message: 'Please specify which task to assign'
    }
  }
  
  const task = event.tasks.find(t => t.name === target.task)
  if (!task) {
    return {
      success: false,
      message: `Task "${target.task}" not found`
    }
  }
  
  const assignee = target.supplier || target.person || 'Unassigned'
  
  // Create notification for new assignment
  const notifications: NotificationTemplate[] = []
  if (target.supplier) {
    const supplier = event.suppliers.find(s => s.name === target.supplier)
    if (supplier) {
      notifications.push({
        recipientId: supplier.id,
        recipientType: 'supplier',
        channel: 'both',
        subject: `New Task Assignment: ${task.name}`,
        message: `You have been assigned to "${task.name}" scheduled at ${task.startTime.toLocaleTimeString()}`,
        priority: 'normal'
      })
    }
  }
  
  return {
    success: true,
    message: `✅ Assigned "${task.name}" to ${assignee}`,
    notifications,
    changes: [`Task reassigned to ${assignee}`]
  }
}

function simulateNotification(
  command: ParsedCommand,
  event: Event
): SimulationResult {
  const { target } = command
  const notifications: NotificationTemplate[] = []
  
  if (target?.person === 'all') {
    // Notify all suppliers
    event.suppliers.forEach(supplier => {
      notifications.push({
        recipientId: supplier.id,
        recipientType: 'supplier',
        channel: 'both',
        subject: 'Schedule Update',
        message: 'Please check the updated schedule for important changes',
        priority: 'normal'
      })
    })
    
    return {
      success: true,
      message: `📤 Notification sent to all ${event.suppliers.length} suppliers`,
      notifications
    }
  }
  
  if (target?.supplier) {
    const supplier = event.suppliers.find(s => s.name === target.supplier)
    if (supplier) {
      notifications.push({
        recipientId: supplier.id,
        recipientType: 'supplier',
        channel: 'both',
        subject: 'Schedule Update',
        message: target.task 
          ? `Update regarding "${target.task}"`
          : 'Please check the updated schedule',
        priority: 'normal'
      })
      
      return {
        success: true,
        message: `📤 Notification sent to ${supplier.name}`,
        notifications
      }
    }
  }
  
  return {
    success: true,
    message: '📤 Notification sent',
    notifications
  }
}

function simulateQuery(
  command: ParsedCommand,
  event: Event,
  currentTime: Date
): SimulationResult {
  const { action } = command
  
  if (action === 'query_current') {
    const currentTasks = event.tasks.filter(t => 
      t.startTime <= currentTime && t.endTime > currentTime
    )
    
    if (currentTasks.length === 0) {
      return {
        success: true,
        message: 'No tasks are currently active'
      }
    }
    
    const taskList = currentTasks.map(t => 
      `• "${t.name}" (${t.status}) - ${t.supplier?.name || 'Unassigned'}`
    ).join('\n')
    
    return {
      success: true,
      message: `Currently active:\n${taskList}`
    }
  }
  
  if (action === 'query_next') {
    const upcomingTasks = event.tasks
      .filter(t => t.startTime > currentTime)
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
      .slice(0, 3)
    
    if (upcomingTasks.length === 0) {
      return {
        success: true,
        message: 'No upcoming tasks scheduled'
      }
    }
    
    const taskList = upcomingTasks.map(t => 
      `• ${t.startTime.toLocaleTimeString()} - "${t.name}" (${t.supplier?.name || 'Unassigned'})`
    ).join('\n')
    
    return {
      success: true,
      message: `Upcoming tasks:\n${taskList}`
    }
  }
  
  // General status query
  const totalTasks = event.tasks.length
  const completedTasks = event.tasks.filter(t => t.status === 'complete').length
  const delayedTasks = event.tasks.filter(t => t.status === 'delayed').length
  const atRiskTasks = event.tasks.filter(t => t.status === 'at_risk').length
  
  return {
    success: true,
    message: `Schedule Status:
• Total tasks: ${totalTasks}
• Completed: ${completedTasks}
• Delayed: ${delayedTasks}
• At risk: ${atRiskTasks}
• Progress: ${Math.round((completedTasks / totalTasks) * 100)}%`
  }
}

function simulateAddTask(
  command: ParsedCommand,
  event: Event
): SimulationResult {
  const { action, target } = command
  
  if (action === 'add_note' && target?.task) {
    const task = event.tasks.find(t => t.name === target.task)
    if (task) {
      return {
        success: true,
        message: `📝 Note added to "${task.name}"`,
        changes: [`Added note: ${target.status || 'Note content'}`]
      }
    }
  }
  
  // Simulate adding a new task
  const newTaskTime = suggestOptimalTime(
    {
      id: `task-new-${Date.now()}`,
      name: 'New Task',
      startTime: new Date(),
      endTime: new Date(Date.now() + 30 * 60000),
      duration: 30,
      status: 'pending',
      priority: 'medium',
      isAnchor: false
    } as Task,
    event.tasks
  )
  
  return {
    success: true,
    message: `✅ New task would be scheduled at ${newTaskTime.toLocaleTimeString()}`,
    changes: ['New task added to schedule']
  }
}

function simulateRemoveTask(
  command: ParsedCommand,
  event: Event
): SimulationResult {
  const { target } = command
  
  if (!target?.task) {
    return {
      success: false,
      message: 'Please specify which task to remove'
    }
  }
  
  const task = event.tasks.find(t => t.name === target.task)
  if (!task) {
    return {
      success: false,
      message: `Task "${target.task}" not found`
    }
  }
  
  if (task.isAnchor) {
    return {
      success: false,
      message: 'Anchor tasks cannot be removed'
    }
  }
  
  // Check for dependencies
  const dependentTasks = event.tasks.filter(t => 
    t.dependencies?.includes(task.id)
  )
  
  const warnings = dependentTasks.length > 0
    ? [`Warning: ${dependentTasks.length} task(s) depend on this task`]
    : []
  
  return {
    success: true,
    message: `⚠️ Task "${task.name}" would be removed from schedule`,
    changes: [
      'Task removed from schedule',
      ...warnings
    ]
  }
}

// Generate realistic response messages
export const generateResponseMessage = (
  result: SimulationResult,
  command: ParsedCommand
): string => {
  if (!result.success) {
    return result.message
  }
  
  const responses = [
    result.message,
    `Processing: ${command.suggestedResponse}`,
    'Change has been simulated successfully',
    'Schedule update prepared for review'
  ]
  
  // Add random variation for more natural feel
  const randomIndex = Math.floor(Math.random() * responses.length)
  return responses[randomIndex] || result.message
}

// Simulate incoming messages from suppliers
export const generateIncomingMessage = (
  event: Event,
  currentTime: Date
): Message => {
  const suppliers = event.suppliers
  const randomSupplier = suppliers[Math.floor(Math.random() * suppliers.length)]
  const sources: Message['source'][] = ['email', 'sms', 'whatsapp']
  const randomSource = sources[Math.floor(Math.random() * sources.length)]
  
  const messages = [
    `Running 10 minutes late due to traffic`,
    `Setup complete, ready for next phase`,
    `Need clarification on timing for ${event.tasks[0]?.name || 'task'}`,
    `All team members have arrived on site`,
    `Equipment issue resolved, back on schedule`,
    `Can we push back by 15 minutes?`,
    `Everything looking good from our end`
  ]
  
  const randomMessage = messages[Math.floor(Math.random() * messages.length)]
  
  return {
    id: `msg-${Date.now()}`,
    source: randomSource,
    from: randomSupplier.name,
    content: randomMessage,
    timestamp: currentTime,
    isProcessed: false,
    messageUnderstandingScore: 0.7 + Math.random() * 0.3
  }
}